/**
 * Mutate the active Prompt Lab session's system prompt.
 *
 * Dispatches the existing `updateSession` Redux action. Local UX only;
 * the session is per-user state, never replicated to Convex.
 *
 * Marked mutatesRemote=false because Redux is *local* state, but
 * requiresApproval=true so the model can't silently overwrite the
 * user's prompt — NIPE must propose, the user confirms in the chat
 * approval lane.
 */

import type { NipeTool } from './types';

export interface SetSystemPromptContext {
  /** Returns the active sessionId so the tool can target the right
   * session even if the user has multiple. Null when Prompt Lab is
   * still booting. */
  getActivePromptSessionId?: () => string | null;
  /** Dispatches `updateSession({ id, changes: { systemPrompt } })`. */
  setPromptSessionSystemPrompt?: (
    sessionId: string,
    text: string,
  ) => { ok: true } | { ok: false; reason: string };
}

const MAX_PROMPT_BYTES = 50_000;

export const setSystemPromptTool: NipeTool<SetSystemPromptContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'setSystemPrompt',
      description:
        "Replace the system prompt on the user's active Prompt Lab session. " +
        'Use this only after the user has agreed to your proposed rewrite, ' +
        'or after they explicitly asked you to write the system prompt for ' +
        'them. Always read the current systemPrompt via readPromptSession ' +
        'first so you can preserve intent the user already encoded.',
      parameters: {
        type: 'object',
        properties: {
          text: {
            type: 'string',
            description:
              'The new system-prompt text. Replaces the existing value ' +
              'verbatim. Plain text, no markdown wrappers. Max 50,000 chars.',
          },
        },
        required: ['text'],
      },
    },
  },
  mutatesRemote: false,
  // The mutation is local-only, but the user's prompt is sacred — the
  // chat surface gates this behind explicit confirmation.
  requiresApproval: true,
  handler: async (args, ctx) => {
    if (!ctx.getActivePromptSessionId || !ctx.setPromptSessionSystemPrompt) {
      return {
        ok: false,
        reason: 'Prompt Lab is not mounted in this session.',
      };
    }
    const text = typeof args.text === 'string' ? args.text : '';
    if (text.length > MAX_PROMPT_BYTES) {
      return {
        ok: false,
        reason: `System prompt too large (${text.length} > ${MAX_PROMPT_BYTES} bytes).`,
      };
    }
    const sessionId = ctx.getActivePromptSessionId();
    if (sessionId === null) {
      return {
        ok: false,
        reason: 'No active prompt session yet (Prompt Lab is still booting).',
      };
    }
    return ctx.setPromptSessionSystemPrompt(sessionId, text);
  },
};
